// ContactMoveHouseholdModal
//
// Phase A1 (May 2026 — data hygiene gap recovery): the contact-page
// "Move to household" action. Backend already had everything needed
// (the data-access fn `moveContactToHousehold` and the endpoint
// POST /api/households/:id/members with mode=move_existing) — only
// the UI was missing.
//
// Mental model:
//   * Same person, wrong household → MOVE the contact (this modal).
//     Activities, notes, tags, contact-roles follow the contact.
//     Household-scoped records (service_records, opportunities,
//     transactions, rhythms) STAY with the original household.
//   * Same person duplicated → MERGE the contact (existing wizard).
//     The duplicate row is sidelined; FKs re-parent.
//   * Different households should be one → MERGE the households
//     (existing household-merge picker).
//
// The plain-English banner above the picker lays out which of the
// three the user actually wants, so they don't pick the wrong tool.

import { useEffect, useState } from "react";

import {
  api,
  ApiError,
  type HouseholdListItem,
  type SafeContact,
  type SplitMovableRecord,
} from "../api";
import { Button } from "../ui";
import { Modal } from "./Modal";
import { SplitRecordsFollowUpDialog } from "./SplitRecordsFollowUpDialog";
import { useToast } from "./Toast";

export interface ContactMoveHouseholdModalProps {
  contact: SafeContact;
  /** The contact's current household name (for the banner copy). null if the contact has no household. */
  currentHouseholdName: string | null;
  onClose: () => void;
  /** Fires after a successful move with the destination household id. */
  onMoved: (destinationHouseholdId: string) => void;
}

type Role = "primary" | "spouse" | "child" | "other" | "member";

const ROLE_OPTIONS: ReadonlyArray<{ value: Role; label: string }> = [
  { value: "member", label: "Member (default)" },
  { value: "spouse", label: "Spouse" },
  { value: "child", label: "Child" },
  { value: "other", label: "Other" },
  { value: "primary", label: "Primary (will replace existing primary)" },
];

export function ContactMoveHouseholdModal({
  contact,
  currentHouseholdName,
  onClose,
  onMoved,
}: ContactMoveHouseholdModalProps) {
  const toast = useToast();
  // "existing" = search-and-pick an existing household (original behavior).
  // "new" = spin up a brand-new household for this contact in one step.
  // The "new" path was previously ONLY reachable from the household
  // Members tab (Give-them-their-own-household), so a user standing on a
  // contact who wanted "put this person in their own household" hit a dead
  // end here — the exact wall Grant reported. It reuses the same
  // splitOutHouseholdMember primitive the Members tab uses.
  const canCreateNew = Boolean(contact.household_id);
  const [mode, setMode] = useState<"existing" | "new">("existing");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<HouseholdListItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  // Set only when a search's ENTIRE result set was the contact's own
  // current household, filtered out below as a no-op destination. Lets
  // the empty-state say "already there" instead of "nothing found" —
  // the dead end Grant hit searching "doran" for a contact already in
  // the Doran household (2026-08-10).
  const [onlyMatchIsCurrent, setOnlyMatchIsCurrent] =
    useState<HouseholdListItem | null>(null);
  const [picked, setPicked] = useState<HouseholdListItem | null>(null);
  const [role, setRole] = useState<Role>("member");
  const [moving, setMoving] = useState(false);
  // Set when the split-to-new path finds records naming the contact on the
  // household they just left — the "should these follow them?" step the
  // household People tab's split already had, but this contact-page path
  // used to skip (it called splitOutHouseholdMember and ignored the
  // movable_records it already returned). Fixed 2026-09-05.
  const [recordsFollowUp, setRecordsFollowUp] = useState<{
    newHouseholdId: string;
    newHouseholdName: string;
    records: SplitMovableRecord[];
  } | null>(null);

  // Debounced search (250ms). Mirrors HouseholdMergePicker's pattern.
  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      setSearching(false);
      setSearchError(null);
      setOnlyMatchIsCurrent(null);
      return;
    }
    setSearching(true);
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await api.listHouseholds({
          q: query.trim(),
          limit: 20,
        });
        if (cancelled) return;
        // Drop the contact's CURRENT household — moving to the same
        // household is a no-op and the user-facing label would say
        // "move to itself" which is confusing.
        const current = res.households.find(
          (h) => h.id === contact.household_id,
        );
        const filtered = res.households.filter(
          (h) => h.id !== contact.household_id,
        );
        setResults(filtered);
        // The whole result set was just the contact's own household —
        // say so, rather than falling through to the generic
        // "No matching households" (which reads as "nothing exists"
        // when the truth is "this is already done").
        setOnlyMatchIsCurrent(
          filtered.length === 0 && current ? current : null,
        );
        setSearchError(null);
      } catch (err) {
        if (cancelled) return;
        setSearchError(
          err instanceof ApiError
            ? `${err.status} — ${err.message}`
            : "Search failed",
        );
        setResults([]);
        setOnlyMatchIsCurrent(null);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, contact.household_id]);

  // "Create a new household for them" — reuses splitOutHouseholdMember,
  // which lifts the contact out of their current household into a fresh
  // one (auto-named from their surname) in a single reversible step.
  async function handleSplitToNew() {
    if (moving || !contact.household_id) return;
    setMoving(true);
    try {
      const res = await api.splitOutHouseholdMember(
        contact.household_id,
        contact.id,
      );
      toast.success(
        `Moved ${displayName(contact)} into a new household ("${res.new_household_name}").`,
      );
      const movable = res.movable_records ?? [];
      if (movable.length > 0) {
        setRecordsFollowUp({
          newHouseholdId: res.new_household_id,
          newHouseholdName: res.new_household_name,
          records: movable,
        });
      } else {
        onMoved(res.new_household_id);
      }
    } catch (err) {
      toast.error(
        err instanceof ApiError
          ? `${err.status} — ${err.message}`
          : "Move failed",
      );
    } finally {
      setMoving(false);
    }
  }

  async function handleMove() {
    if (!picked || moving) return;
    // 2026-05-24 (audit-sweep PR E): the old window.confirm here was
    // redundant. The modal body already shows the same consequences
    // inline above the "Move to {picked}" button (see the role="status"
    // block below), so the user has read them at the moment of
    // decision — a second pop-up confirm just doubles the friction
    // for no extra clarity. Clicking the Move button while the
    // consequences are visible IS the confirmation.
    setMoving(true);
    try {
      const result = await api.addHouseholdMemberMove(picked.id, {
        contact_id: contact.id,
        role,
      });
      const vacated = result.vacated_primary_household_id;
      toast.success(
        vacated
          ? `Moved. The previous household lost its primary slot — designate a new primary.`
          : `Moved to "${picked.display_name}".`,
      );
      onMoved(picked.id);
    } catch (err) {
      toast.error(
        err instanceof ApiError
          ? `${err.status} — ${err.message}`
          : "Move failed",
      );
    } finally {
      setMoving(false);
    }
  }

  if (recordsFollowUp) {
    return (
      <SplitRecordsFollowUpDialog
        personName={displayName(contact)}
        newHouseholdId={recordsFollowUp.newHouseholdId}
        newHouseholdName={recordsFollowUp.newHouseholdName}
        records={recordsFollowUp.records}
        onClose={() => {
          setRecordsFollowUp(null);
          onMoved(recordsFollowUp.newHouseholdId);
        }}
        onMoved={() => {
          setRecordsFollowUp(null);
          onMoved(recordsFollowUp.newHouseholdId);
        }}
      />
    );
  }

  return (
    <Modal
      open={true}
      title={`Move ${displayName(contact)} to a different household`}
      onClose={moving ? () => undefined : onClose}
      className="modal-wide"
      footer={
        mode === "new" ? (
          <>
            <Button variant="ghost" onClick={onClose} disabled={moving}>
              Cancel
            </Button>
            <Button
              onClick={() => void handleSplitToNew()}
              disabled={moving || !canCreateNew}
            >
              {moving ? "Moving…" : "Move to a new household"}
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={moving}>
              Cancel
            </Button>
            <Button onClick={handleMove} disabled={!picked || moving}>
              {moving
                ? "Moving…"
                : picked
                  ? `Move to "${picked.display_name}"`
                  : "Pick a destination first"}
            </Button>
          </>
        )
      }
    >
      <div
        role="status"
        style={{
          background: "rgba(74, 152, 198, 0.08)",
          border: "1px solid rgba(74, 152, 198, 0.3)",
          borderRadius: 6,
          padding: "10px 12px",
          fontSize: 12,
          marginBottom: 12,
          lineHeight: 1.5,
        }}
      >
        <strong>This MOVES the contact, it doesn't merge anything.</strong>
        <ul
          style={{
            margin: "6px 0 0",
            paddingLeft: 18,
          }}
        >
          <li>
            The contact's row is preserved as-is. Activities, tags, notes,
            and contact-roles follow them.
          </li>
          <li>
            Household-scoped records (service records, opportunities,
            transactions, rhythms) <strong>stay</strong> with{" "}
            <strong>
              {currentHouseholdName ?? "the source household"}
            </strong>
            . If you want those to move too, run{" "}
            <strong>Merge households</strong> instead.
          </li>
          <li>
            If this contact is currently the primary of{" "}
            <strong>
              {currentHouseholdName ?? "the source household"}
            </strong>
            , that household's primary slot is cleared and someone else
            needs to be designated.
          </li>
        </ul>
      </div>

      {/* Destination mode: an existing household vs a brand-new one. */}
      <fieldset
        style={{ border: "none", padding: 0, margin: "0 0 12px", display: "grid", gap: 8 }}
      >
        <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13 }}>
          <input
            type="radio"
            name="move-destination-mode"
            checked={mode === "existing"}
            disabled={moving}
            onChange={() => setMode("existing")}
          />
          <span>
            <strong>An existing household</strong>
            <span className="muted"> — search for and pick one below.</span>
          </span>
        </label>
        <label
          style={{
            display: "flex",
            gap: 8,
            alignItems: "flex-start",
            fontSize: 13,
            opacity: canCreateNew ? 1 : 0.5,
          }}
        >
          <input
            type="radio"
            name="move-destination-mode"
            checked={mode === "new"}
            disabled={moving || !canCreateNew}
            onChange={() => setMode("new")}
          />
          <span>
            <strong>A brand-new household</strong>
            <span className="muted">
              {canCreateNew
                ? " — create a fresh household just for them, in one step."
                : " — unavailable: this contact isn't in a household yet."}
            </span>
          </span>
        </label>
      </fieldset>

      {mode === "new" ? (
        <p style={{ fontSize: 13, lineHeight: 1.5, margin: 0 }}>
          A new household will be created and{" "}
          <strong>{displayName(contact)}</strong> moved into it (named after
          their surname — you can rename it afterward). This is reversible.
        </p>
      ) : (
        <>
      <label style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
        Search for the destination household
      </label>
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Type at least 2 characters…"
        style={{
          width: "100%",
          padding: "6px 8px",
          border: "1px solid var(--mid-gray)",
          borderRadius: "var(--radius-compact)",
          fontSize: 14,
        }}
        autoFocus
      />

      {searching && (
        <p className="muted" style={{ fontSize: 12, margin: "6px 0 0" }}>
          Searching…
        </p>
      )}
      {searchError && (
        <p style={{ color: "var(--danger)", fontSize: 12, margin: "6px 0 0" }}>
          {searchError}
        </p>
      )}
      {!searching && onlyMatchIsCurrent && (
        <div
          style={{
            marginTop: 8,
            padding: "8px 12px",
            border: "1px solid var(--mid-gray)",
            borderRadius: "var(--radius-compact)",
            background: "var(--light-gray)",
          }}
        >
          <p style={{ fontSize: 13, margin: 0 }}>
            <strong>{displayName(contact)}</strong> is already in the{" "}
            <strong>{onlyMatchIsCurrent.display_name}</strong> household —
            that's the only match for "{query.trim()}". Nothing to move.
          </p>
        </div>
      )}
      {!searching &&
        !onlyMatchIsCurrent &&
        query.trim().length >= 2 &&
        results.length === 0 && (
          <p className="muted" style={{ fontSize: 12, margin: "6px 0 0" }}>
            No matching households.
          </p>
        )}

      {results.length > 0 && (
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: "8px 0 0",
            maxHeight: 240,
            overflowY: "auto",
            border: "1px solid var(--mid-gray)",
            borderRadius: "var(--radius-compact)",
          }}
        >
          {results.map((h) => {
            const isPicked = picked?.id === h.id;
            return (
              <li key={h.id}>
                <button
                  type="button"
                  onClick={() => setPicked(h)}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    background: isPicked
                      ? "rgba(74, 152, 198, 0.12)"
                      : "transparent",
                    border: "none",
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontSize: 13,
                    borderBottom: "1px solid var(--mid-gray)",
                  }}
                >
                  <div style={{ fontWeight: 600, color: "var(--navy)" }}>
                    {h.display_name}
                  </div>
                  {/* WHO is in this household (Nate 2026-07-14, N17) — same
                      wrong-household trap as the record-move picker: show
                      member names so identically-named households are
                      tellable apart. */}
                  {h.members_label && (
                    <div className="muted" style={{ fontSize: 11.5 }}>
                      {h.members_label}
                    </div>
                  )}
                  <div className="muted" style={{ fontSize: 11 }}>
                    {h.contact_count}{" "}
                    {h.contact_count === 1 ? "contact" : "contacts"}
                    {h.primary_city &&
                      h.primary_state &&
                      ` · ${h.primary_city}, ${h.primary_state}`}
                    {h.company_slugs.length > 0 &&
                      ` · ${h.company_slugs.join(", ")}`}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {picked && (
        <div style={{ marginTop: 12 }}>
          <label
            htmlFor="contact-move-role"
            style={{ display: "block", fontSize: 13, marginBottom: 4 }}
          >
            Role in the new household
          </label>
          <select
            id="contact-move-role"
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            disabled={moving}
            style={{
              width: "100%",
              padding: "6px 8px",
              border: "1px solid var(--mid-gray)",
              borderRadius: "var(--radius-compact)",
              fontSize: 14,
            }}
          >
            {ROLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      )}
        </>
      )}
    </Modal>
  );
}

function displayName(c: SafeContact): string {
  const first = c.preferred_name ?? c.legal_first_name;
  const last = c.legal_last_name;
  const joined = [first, last].filter(Boolean).join(" ").trim();
  return joined === "" ? "(no name on file)" : joined;
}
